import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./recommendations.module.css";

import Sidebar from "../../components/sidebar/sidebar";
import { useUserProfile } from "../../components/context/UserProfileContext";
import { getProfileImageUrl, resolveProfileId } from "../../../../utils/serverUtils";
import { API_URL } from "../../../../api";
import { useCookies } from "../../../../hooks/useCookies";

export default function Recommendations() {
  const { profiles, isPremium } = useUserProfile(); // 👈 get isPremium from context
  const navigate = useNavigate();
  // ... existing code ...
  const observerRef = useRef();

  const [allProfiles, setAllProfiles] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagingWave, setPagingWave] = useState(null); // 👈 wave used for pagination (2+)

  const cookies = useCookies();
  const token = cookies.getCookie("token");

  // Decide wave list for scrolling
  const getWaveList = (premiumFlag) => {
    if (premiumFlag) {
      // premium: wave 1 → 2 → 3
      return [1, 2, 3];
    }
    // non-premium: only wave 0
    return [0];
  };

  // 🔄 Sync first page from context into local list
  useEffect(() => {
    if (Array.isArray(profiles) && profiles.length > 0) {
      setAllProfiles(profiles);
      setHasMore(true);
      setCurrentPage(1);
    } else {
      setAllProfiles([]);
      setHasMore(false);
    }
  }, [profiles]);

  // Initialize pagingWave once isPremium is known
  useEffect(() => {
    if (isPremium === null || isPremium === undefined) return;
    const waves = getWaveList(isPremium);
    setPagingWave(waves[0]); // premium: 1, non-premium: 0
  }, [isPremium]);

  const fetchMoreProfiles = useCallback(
    async (page) => {
      if (!token || isLoadingMore || !hasMore) return;
      if (isPremium === null || isPremium === undefined) return; // wait until we know
      if (pagingWave === null || pagingWave === undefined) return;

      const waves = getWaveList(isPremium);
      if (!waves.length) return;

      setIsLoadingMore(true);
      try {
        let waveToTry = pagingWave;
        if (!waves.includes(waveToTry)) {
          waveToTry = waves[0];
        }

        let pageToTry = page;

        while (true) {
          const url = `${API_URL}customer/user/profiles?page=${pageToTry}&wave=${waveToTry}`;
          // console.log("[RECO] FETCH", { url });

          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();
          const newResults = data?.results || [];

          if (response.ok && newResults.length > 0) {
            // Frontend Shuffle: Randomize the order of profiles before displaying
            for (let i = newResults.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newResults[i], newResults[j]] = [newResults[j], newResults[i]];
            }

            // ✅ we got data for (pageToTry, waveToTry)
            setAllProfiles((prev) => {
              const existingProfilesMap = new Map(
                prev.map((p) => [p.user_id, p])
              );
              newResults.forEach((profile) => {
                if (!existingProfilesMap.has(profile.user_id)) {
                  existingProfilesMap.set(profile.user_id, profile);
                }
              });
              return Array.from(existingProfilesMap.values());
            });

            setPagingWave(waveToTry); // remember the wave we successfully used
            setCurrentPage(pageToTry);
            setHasMore(true);
            break;
          } else {
            // ❌ empty page for this wave
            const idx = waves.indexOf(waveToTry);
            const nextWave = waves[idx + 1];

            if (nextWave == null) {
              // no more waves to try
              setHasMore(false);
              break;
            }

            // move to next wave, start from page 1 there
            waveToTry = nextWave;
            pageToTry = 1;
          }
        }
      } catch (error) {
        console.error("Error fetching more profiles:", error);
        setHasMore(false);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [token, isLoadingMore, hasMore, isPremium, pagingWave]
  );

  const lastProfileElementRef = useCallback(
    (node) => {
      if (isLoadingMore || !hasMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          fetchMoreProfiles(nextPage);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoadingMore, hasMore, currentPage, fetchMoreProfiles]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <Sidebar>
      <div className={styles["children"]}>
        {allProfiles?.length === 0 ? (
          <div className={styles.emptyState}>
            <h4 className={styles.emptyStateTitle}>No new profiles</h4>

            <p className={styles.emptyStateText}>
              Please update your Preferences to discover more users!
            </p>

            <button
              className={styles.emptyStateButton}
              onClick={() => navigate("/user/preferences")}
            >
              Update Preferences
            </button>
          </div>
        ) : (
          <div className={styles["recommendations"]}>
            <div
              style={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <p style={{ fontWeight: "600", fontSize: "large" }}>
                Recommendations
              </p>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => navigate("/user/preferences")}
                style={{ cursor: "pointer" }}
              >
                <path
                  d="M4 5L10 5M10 5C10 6.10457 10.8954 7 12 7C13.1046 7 14 6.10457 14 5M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5M14 5L20 5M4 12H16M16 12C16 13.1046 16.8954 14 18 14C19.1046 14 20 13.1046 20 12C20 10.8954 19.1046 10 18 10C16.8954 10 16 10.8954 16 12ZM8 19H20M8 19C8 17.8954 7.10457 17 6 17C4.89543 17 4 17.8954 4 19C4 20.1046 4.89543 21 6 21C7.10457 21 8 20.1046 8 19Z"
                  stroke="#5E5E5E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {allProfiles.map((profile, index) => (
              <div
                key={profile.user_id}
                ref={
                  index === allProfiles.length - 1
                    ? lastProfileElementRef
                    : null
                }
                className={styles["recommended-card"]}
                style={{
                  backgroundImage: `url(${getProfileImageUrl(profile)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(
                    `/user/${resolveProfileId(profile)}?token=${encodeURIComponent(
                      profile.profile_token || ""
                    )}`,
                    {
                      state: {
                        profile_token: profile.profile_token,
                      },
                    }
                  )
                }
              >
                <div>
                  <p
                    style={{
                      fontWeight: "600",
                      fontSize: "large",
                    }}
                  >
                    {profile.first_name},{" "}
                    {dayjs().diff(profile.birthday, "year")}
                  </p>
                  <p>{profile.job}</p>
                </div>
              </div>
            ))}

            {isLoadingMore && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "2rem",
                }}
              >
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {!hasMore && allProfiles.length > 0 && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "2rem",
                  color: "#666",
                }}
              >
                <p>No more profiles to show</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Sidebar>
  );
}
